import { useEffect, useRef } from "react";
import { cjsDefault } from "../../lib/cjs-default";
import reactFadeIn from "react-fade-in";
import { useHistory, useLocation } from "react-router-dom";
import { displayToast } from "../../components/app/AppToast";
import {
  HeaderBoxAutoSafe,
  HeaderLinks,
  HeaderTitle,
  NavBack,
} from "../../components/app/headers/layout";
import RoomQuizCard from "../../components/cards/RoomQuizCard";
import SkeletonContentCard from "../../components/profile/SkeletonContentCard";
import { RoomListWrapper } from "../../components/room/layout";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { usePageScroll } from "../../domains/app/hooks/usePageScroll";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useApiQuery } from "../../services/api";
import type { PrivateRoom } from "../../domains/room/privateRoom";

import { useTranslation } from "react-i18next";

const FadeIn = cjsDefault(reactFadeIn);

export default function RoomQuizListPage({
  roomId,
  room,
}: {
  roomId: string;
  room: PrivateRoom;
}) {
  const history = useHistory();
  const location = useLocation();
  const { activeLang } = useActiveLang();
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { savePageScrollY, restorePageScrollY } = usePageScroll(scrollRef);

  const eventId = new URLSearchParams(location.search).get("event");

  const result = useApiQuery(
    "/webapp/private/rooms/:id/quizzes",
    roomId !== null && eventId !== null
      ? { param: { id: roomId }, query: { event: eventId } }
      : false
  );

  useEffect(() => {
    restorePageScrollY();
  }, []);

  useEffect(() => {
    if (eventId === null) {
      history.goBack();
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }, [eventId]);

  useEffect(() => {
    if (result.state === "error") {
      console.error("Couldn't load room.", result.message);
      history.push("/discovery");
    }
  }, [result.state]);

  const isLoading = result.state === "pending";

  return (
    <ScrollableLayout
      scrollRef={scrollRef}
      header={
        <HeaderBoxAutoSafe>
          <HeaderLinks>
            <NavBack />
          </HeaderLinks>
          <HeaderTitle>{t("Quiz")}</HeaderTitle>
          <HeaderLinks />
        </HeaderBoxAutoSafe>
      }
    >
      <div className="min-h-dvh">
        {isLoading ? (
          <FadeIn key="loading">
            <RoomListWrapper style={{ marginTop: 16, gap: 10 }}>
              {Array.from({ length: 10 }).map((_, index) => (
                <SkeletonContentCard key={index} />
              ))}
            </RoomListWrapper>
          </FadeIn>
        ) : result.state === "success" && roomId !== null ? (
          <FadeIn key="content">
            <RoomListWrapper style={{ marginTop: 16, gap: 10 }}>
              {result.body.length === 0 && <p>{t("No content")}</p>}

              {result.body.map((quiz, index) => {
                const rawTranslation =
                  quiz.translations[activeLang] ?? quiz.translations.en;

                if (!rawTranslation) {
                  return null;
                }

                const eventName =
                  quiz.event.translations?.[activeLang]?.eventName ??
                  quiz.event.translations?.en?.eventName ??
                  null;

                return (
                  <RoomQuizCard
                    key={quiz.id}
                    index={index}
                    room={room}
                    translation={{
                      title: rawTranslation.title,
                      subtitle: rawTranslation.subtitle,
                    }}
                    eventName={eventName}
                    onclick={() => {
                      savePageScrollY();
                      history.push(`/room/quiz/${roomId}/${quiz.template}`);
                    }}
                  />
                );
              })}
            </RoomListWrapper>
          </FadeIn>
        ) : null}
      </div>
    </ScrollableLayout>
  );
}
